/** Minimal History-API router: the app has a handful of flat routes. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

interface RouterValue {
  path: string;
  navigate: (to: string, options?: { replace?: boolean }) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [path, setPath] = useState<string>(() => window.location.pathname);

  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((to: string, options?: { replace?: boolean }) => {
    if (to === window.location.pathname) return;
    if (options?.replace) window.history.replaceState(null, '', to);
    else window.history.pushState(null, '', to);
    setPath(to);
    window.scrollTo(0, 0);
  }, []);

  const value = useMemo(() => ({ path, navigate }), [path, navigate]);
  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const value = useContext(RouterContext);
  if (!value) throw new Error('useRouter must be used inside RouterProvider');
  return value;
}

export const routes = {
  home: '/',
  words: '/words',
  chat: '/chat',
  league: '/league',
  profile: '/profile',
  quick: '/quick',
  placement: '/placement',
  lesson: (id: string) => `/lesson/${encodeURIComponent(id)}`,
} as const;

/** Lesson id from a /lesson/:id path, or null. */
export function lessonIdFromPath(path: string): string | null {
  const match = /^\/lesson\/([^/]+)$/.exec(path);
  return match ? decodeURIComponent(match[1]!) : null;
}
